import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Button } from '../common';

/**
 * SignaturePad — Componentă pentru captură semnătură pe pad fizic sau mouse
 * Compatibil cu: Wacom STU/DTU, orice tabletă grafică, mouse, touch screen
 *
 * Props:
 *   onSave(svgData, biometricData) — callback când utilizatorul acceptă semnătura
 *   onCancel() — callback la anulare
 *   width, height — dimensiuni canvas
 *   label — eticheta afișată deasupra
 */
export default function SignaturePad({
  onSave,
  onCancel,
  width  = 500,
  height = 200,
  label  = 'Semnați în spațiul de mai jos',
}) {
  const canvasRef  = useRef(null);
  const [desenam,  setDesenam]  = useState(false);
  const [are_date, setAreDDate] = useState(false);

  // Date biometrice: coordonate, timpi, presiune
  const biometricRef = useRef({ points: [], timings: [], pressure: [] });
  const lastPoint    = useRef(null);

  // ── Inițializare canvas ───────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth   = 2;
    ctx.lineCap     = 'round';
    ctx.lineJoin    = 'round';
    ctx.fillStyle   = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  // ── Obținere coordonate relative la canvas ────────────────
  function getPos(e) {
    const canvas = canvasRef.current;
    const rect   = canvas.getBoundingClientRect();
    const scaleX = canvas.width  / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches) {
      const touch = e.touches[0];
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top)  * scaleY,
        pressure: touch.force || 0.5,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top)  * scaleY,
      pressure: e.pressure || 0.5,
    };
  }

  // ── Start desen ───────────────────────────────────────────
  const startDesen = useCallback((e) => {
    e.preventDefault();
    setDesenam(true);
    setAreDDate(true);

    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    const pos    = getPos(e);

    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);

    lastPoint.current = pos;
    biometricRef.current.points.push({ x: pos.x, y: pos.y });
    biometricRef.current.timings.push(Date.now());
    biometricRef.current.pressure.push(pos.pressure);
  }, []);

  // ── Desen continuu ─────────────────────────────────────────
  const desen = useCallback((e) => {
    if (!desenam) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    const pos    = getPos(e);

    // Linie curbă pentru aspect natural
    if (lastPoint.current) {
      ctx.beginPath();
      ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
      ctx.lineWidth = 1.5 + pos.pressure * 1.5; // variație grosime cu presiunea
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    }

    lastPoint.current = pos;
    biometricRef.current.points.push({ x: pos.x, y: pos.y });
    biometricRef.current.timings.push(Date.now());
    biometricRef.current.pressure.push(pos.pressure);
  }, [desenam]);

  // ── Stop desen ────────────────────────────────────────────
  const stopDesen = useCallback((e) => {
    if (e) e.preventDefault();
    setDesenam(false);
    lastPoint.current = null;
  }, []);

  // ── Curățare canvas ───────────────────────────────────────
  function curata() {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    biometricRef.current = { points: [], timings: [], pressure: [] };
    setAreDDate(false);
  }

  // ── Salvare semnătură ─────────────────────────────────────
  function salveaza() {
    if (!are_date) {
      alert('Vă rugăm să semnați înainte de a confirma');
      return;
    }

    const canvas  = canvasRef.current;

    // Exportăm ca SVG (vectorial — mai bun pentru documente)
    const svgData = canvasToSVG(canvas, biometricRef.current.points);

    onSave(svgData, {
      points:   biometricRef.current.points,
      timings:  biometricRef.current.timings,
      pressure: biometricRef.current.pressure,
      duration: biometricRef.current.timings.length > 1
        ? biometricRef.current.timings[biometricRef.current.timings.length - 1] - biometricRef.current.timings[0]
        : 0,
    });
  }

  return (
    <div style={s.container}>
      <p style={s.label}>{label}</p>

      {/* Indicație dispozitiv */}
      <div style={s.deviceHint}>
        <span>🖊</span>
        <span>Conectați tableta Wacom și semnați direct pe ea, sau semnați cu mouse-ul</span>
      </div>

      {/* Canvas principal */}
      <div style={s.canvasWrap}>
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          style={s.canvas}
          onMouseDown={startDesen}
          onMouseMove={desen}
          onMouseUp={stopDesen}
          onMouseLeave={stopDesen}
          onTouchStart={startDesen}
          onTouchMove={desen}
          onTouchEnd={stopDesen}
        />

        {/* Linie de bază */}
        <div style={s.linie} />
        <span style={s.xMark}>✕</span>
      </div>

      <p style={s.hint}>
        {are_date
          ? '✓ Semnătură înregistrată — apăsați Confirmă pentru a aplica'
          : 'Zona de semnătură — desenați semnătura dvs. în spațiul de mai sus'
        }
      </p>

      {/* Butoane */}
      <div style={s.butoane}>
        <Button onClick={salveaza} disabled={!are_date}>
          ✓ Confirmă semnătura
        </Button>
        <Button onClick={curata} variant="ghost">
          ↺ Șterge și repetă
        </Button>
        {onCancel && (
          <Button onClick={onCancel} variant="ghost">
            Anulează
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Conversie canvas → SVG ────────────────────────────────────
function canvasToSVG(canvas, points) {
  if (!points || points.length < 2) return '<svg></svg>';

  // Construim path SVG din punctele colectate
  let pathD = `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;
  for (let i = 1; i < points.length; i++) {
    pathD += ` L ${points[i].x.toFixed(1)} ${points[i].y.toFixed(1)}`;
  }

  return `<svg xmlns="http://www.w3.org/2000/svg"
    width="${canvas.width}" height="${canvas.height}"
    viewBox="0 0 ${canvas.width} ${canvas.height}">
    <rect width="100%" height="100%" fill="white"/>
    <path d="${pathD}"
      stroke="#1a1a2e" stroke-width="2"
      fill="none" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;
}

const s = {
  container:  { display: 'flex', flexDirection: 'column', gap: '12px' },
  label:      { margin: 0, fontSize: '14px', fontWeight: '600', color: '#333' },
  deviceHint: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', backgroundColor: '#eff6ff', borderRadius: '8px', fontSize: '13px', color: '#1d4ed8' },
  canvasWrap: { position: 'relative', border: '2px solid #d1d5db', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#fff', cursor: 'crosshair' },
  canvas:     { display: 'block', width: '100%', touchAction: 'none' },
  linie:      { position: 'absolute', bottom: '30px', left: '40px', right: '40px', height: '1px', backgroundColor: '#d1d5db', pointerEvents: 'none' },
  xMark:      { position: 'absolute', bottom: '28px', left: '16px', fontSize: '18px', color: '#9ca3af', pointerEvents: 'none' },
  hint:       { margin: 0, fontSize: '12px', color: '#6b7280', textAlign: 'center' },
  butoane:    { display: 'flex', gap: '8px', flexWrap: 'wrap' },
};
